// PanelViewer3D.tsx — Three.js 3D panel viewer using @react-three/fiber
// Renders the active panel as an extruded 3D concrete plate with:
//   - Rectangular void cut-outs for openings (RO, windows, doors, dock levelers)
//   - Circular voids for round blockouts
//   - Feature strip recesses for horizontal/vertical reveals
//   - Camera presets (front/back/left/right/top/iso variants)
//   - Shading modes: wireframe, hidden line (solid gray), shaded (PBR material)
//
// CSG boolean operations (manifold-3d) are optional — falls back to additive
// mesh overlay if manifold is not loaded.

import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Grid, Environment } from '@react-three/drei';
import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import * as THREE from 'three';
import NorthArrow from './NorthArrow';
import GridLabels, { snapGridExtent } from './GridLabels';
import PanelMesh3D from './PanelMesh3D';
import { useShallowStore } from '../../store/useStore';
import type { ViewPreset, ShadingMode } from '../../types/panelvar';
import { computeEdgeTrims } from '../../utils/buildingFootprint';

// ── Camera preset directions (unit-ish vectors, normalised in goToPreset) ───
const PRESET_DIR: Record<ViewPreset, [number, number, number]> = {
  'front':   [0,    0,     1],
  'back':    [0,    0,    -1],
  'left':    [-1,   0,     0],
  'right':   [1,    0,     0],
  'top':     [0,    1,     0.001],
  'bottom':  [0,   -1,     0.001],
  'br-iso':  [1,    0.67,  1],
  'bl-iso':  [-1,   0.67,  1],
  'fr-iso':  [1,    0.67, -1],
  'fl-iso':  [-1,   0.67, -1],
};

// Fit distance so panel fills the viewport with 25% padding
function fitDistance(w: number, h: number, fovDeg = 45, aspect = 1): number {
  const halfTan = Math.tan((fovDeg * Math.PI / 180) / 2);
  return Math.max((h / 2) / halfTan, (w / 2) / (aspect * halfTan)) * 1.4;
}

// ── CameraLock — zero-drift camera with raw pointer orbit/zoom ──────────────
// NO OrbitControls library. Camera position is stored as spherical coords in a
// ref. useFrame enforces the exact cartesian position every frame. Mouse orbit
// and wheel zoom update the stored spherical values directly — no library
// round-trips, no accumulators, no drift. Period.
interface CamState {
  theta: number;  // azimuthal angle (radians)
  phi: number;    // polar angle (radians)
  dist: number;   // distance from origin
  near: number;
  far: number;
  tx: number;     // pan target X
  ty: number;     // pan target Y
  tz: number;     // pan target Z
}

function sphericalToCartesian(theta: number, phi: number, dist: number): [number, number, number] {
  const sp = Math.sin(phi);
  return [
    dist * sp * Math.sin(theta),
    dist * Math.cos(phi),
    dist * sp * Math.cos(theta),
  ];
}

function CameraLock({
  preset,
  panelId,
  panelSize,
  resetKey,
  debugLog,
  zoomRef,
  panRef,
  onSelect,
  onClickEmpty,
  onBoxSelectUpdate,
  onBoxSelectEntities,
  onManualMove,
  onDblClickEntity,
  onDblClickPanel,
  onRightClickEntity,
  onRightClickPanel,
}: {
  preset: ViewPreset | null;
  panelId: string | undefined;
  panelSize: React.MutableRefObject<{ w: number; h: number }>;
  resetKey: number;
  debugLog: React.MutableRefObject<string[]>;
  zoomRef: React.MutableRefObject<((factor: number) => void) | null>;
  panRef: React.MutableRefObject<((dx: number, dy: number) => void) | null>;
  onSelect: (entityId: string, ctrlKey: boolean) => void;
  onClickEmpty: () => void;
  onBoxSelectUpdate: (box: { x: number; y: number; w: number; h: number } | null) => void;
  onBoxSelectEntities: (entityIds: string[], ctrlKey: boolean) => void;
  onManualMove?: () => void;
  onDblClickEntity: (entityId: string) => void;
  onDblClickPanel: (screenX: number, screenY: number, panelX: number, panelY: number) => void;
  onRightClickEntity: (entityId: string, screenX: number, screenY: number, panelX: number, panelY: number) => void;
  onRightClickPanel: (screenX: number, screenY: number, panelX: number, panelY: number) => void;
}) {
  const { camera, gl, scene, invalidate } = useThree();
  const state = useRef<CamState>({ theta: 0, phi: Math.PI / 2, dist: 500, near: 0.1, far: 1_000_000, tx: 0, ty: 0, tz: 0 });
  const dragging = useRef(false);
  const dragMode = useRef<'none' | 'orbit' | 'pan' | 'zoom' | 'boxSelect'>('none');
  const boxStart = useRef<{ x: number; y: number } | null>(null);
  const lastPointer = useRef({ x: 0, y: 0 });
  const clickStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const raycaster = useRef(new THREE.Raycaster());
  const frameCount = useRef(0);

  // Expose zoom function to parent
  useEffect(() => {
    zoomRef.current = (factor: number) => {
      state.current.dist = Math.max(1, Math.min(1_000_000, state.current.dist * factor));
      invalidate();
    };
    return () => { zoomRef.current = null; };
  }, [zoomRef, invalidate]);

  // Expose pan function to parent (in screen-space units)
  useEffect(() => {
    panRef.current = (dx: number, dy: number) => {
      const s = state.current;
      const panSpeed = s.dist * 0.002;
      // Camera right vector
      const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
      // Camera up vector (projected)
      const cp = Math.cos(s.phi);
      const sp = Math.sin(s.phi);
      const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
      s.tx += (-dx * rx + dy * ux) * panSpeed;
      s.ty += dy * uy * panSpeed;
      s.tz += (-dx * rz + dy * uz) * panSpeed;
      invalidate();
    };
    return () => { panRef.current = null; };
  }, [panRef, invalidate]);

  // Compute spherical from preset direction vector
  useEffect(() => {
    if (!preset) return;
    const { w, h } = panelSize.current;
    if (!w || !h) return;

    const cam = camera as THREE.PerspectiveCamera;
    const el = gl.domElement;
    const aspect = el.clientWidth > 0 && el.clientHeight > 0
      ? el.clientWidth / el.clientHeight
      : (cam.aspect > 0 ? cam.aspect : 1);
    const dist = fitDistance(w, h, cam.fov ?? 45, aspect);
    if (!Number.isFinite(dist) || dist <= 0) return;

    const [dx, dy, dz] = PRESET_DIR[preset];
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    const nx = dx / len, ny = dy / len, nz = dz / len;

    // Convert direction to spherical
    const theta = Math.atan2(nx, nz);
    const phi = Math.acos(Math.max(-1, Math.min(1, ny)));

    state.current = {
      theta,
      phi: Math.max(0.01, Math.min(Math.PI - 0.01, phi)),
      dist,
      near: dist * 0.001,
      far: dist * 50,
      tx: w / 2, ty: h / 2, tz: 0,
    };

    // Apply immediately
    const [px, py, pz] = sphericalToCartesian(theta, phi, dist);
    camera.position.set(px + w / 2, py + h / 2, pz);
    camera.lookAt(w / 2, h / 2, 0);
    cam.near = state.current.near;
    cam.far = state.current.far;
    cam.updateProjectionMatrix();
    invalidate(); // tell R3F to render one frame

    const msg = `[PRESET] ${preset} theta=${theta.toFixed(3)} phi=${phi.toFixed(3)} dist=${dist.toFixed(0)} pos=(${px.toFixed(1)},${py.toFixed(1)},${pz.toFixed(1)}) aspect=${aspect.toFixed(2)}`;
    debugLog.current.push(msg);
  }, [preset, panelId, resetKey, camera, gl, panelSize, debugLog, invalidate]);

  // Enforce camera position on every render frame + log drift detection
  useFrame(() => {
    frameCount.current++;
    const s = state.current;
    const [ox, oy, oz] = sphericalToCartesian(s.theta, s.phi, s.dist);
    const px = ox + s.tx, py = oy + s.ty, pz = oz + s.tz;

    // Check if camera was moved by something else since last frame
    const cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
    const dx = Math.abs(cx - px), dy = Math.abs(cy - py), dz = Math.abs(cz - pz);
    if (dx > 0.001 || dy > 0.001 || dz > 0.001) {
      const msg = `[DRIFT!] frame=${frameCount.current} camera was at (${cx.toFixed(2)},${cy.toFixed(2)},${cz.toFixed(2)}) expected (${px.toFixed(2)},${py.toFixed(2)},${pz.toFixed(2)}) delta=(${dx.toFixed(4)},${dy.toFixed(4)},${dz.toFixed(4)})`;
      debugLog.current.push(msg);
    }

    // Slam position regardless
    camera.position.set(px, py, pz);
    camera.lookAt(s.tx, s.ty, s.tz);
  });

  // AutoCAD-style mouse controls:
  //   Left click (< 4px)        = Select entity (raycast)
  //   Ctrl + Left click          = Toggle multi-select
  //   Left button drag (≥ 4px)  = Box select (rubber-band)
  //   Shift + Left drag         = Orbit   (laptop-friendly)
  //   Ctrl  + Left drag         = Zoom    (laptop-friendly: up=in, down=out)
  //   Middle button drag        = Pan
  //   Shift + Middle drag       = Orbit
  //   Ctrl+Shift + Middle drag  = Free orbit (unconstrained)
  //   Right button drag         = Orbit
  //   Scroll wheel              = Zoom
  useEffect(() => {
    const el = gl.domElement;
    const inv = invalidate;
    const CLICK_THRESHOLD = 4; // px — below this is a click, above is a drag

    // Double-click tracking
    let lastClickTime = 0;
    let lastClickEid: string | null = null;

    // Long-press tracking for touch
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let longPressStart: { x: number; y: number } | null = null;

    function cancelLongPress() {
      if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
      longPressStart = null;
    }

    // Walk up the object tree to find the first userData.entityId
    function findEntityId(obj: THREE.Object3D | null): string | null {
      while (obj) {
        if (obj.userData?.entityId) return obj.userData.entityId as string;
        obj = obj.parent;
      }
      return null;
    }

    function doRaycast(clientX: number, clientY: number, ctrlKey: boolean) {
      const rect = el.getBoundingClientRect();
      const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const hits = raycaster.current.intersectObjects(scene.children, true);

      let hitEid: string | null = null;
      let hitPoint: THREE.Vector3 | null = null;
      for (const hit of hits) {
        const eid = findEntityId(hit.object);
        if (eid) {
          hitEid = eid;
          hitPoint = hit.point;
          break;
        }
      }

      const now = Date.now();
      const isDbl = (now - lastClickTime < 400) && hitEid === lastClickEid;

      if (hitEid && hitEid !== 'panel-body') {
        if (isDbl) {
          onDblClickEntity(hitEid);
        } else {
          onSelect(hitEid, ctrlKey);
        }
      } else if (hitPoint) {
        // Hit panel body or nothing with entity
        if (isDbl) {
          const hw = panelSize.current.w / 2;
          const hh = panelSize.current.h / 2;
          onDblClickPanel(clientX, clientY, hitPoint.x + hw, hitPoint.y + hh);
        } else {
          onClickEmpty();
        }
      } else {
        onClickEmpty();
      }

      lastClickTime = now;
      lastClickEid = hitEid;
    }

    /** Raycast to find entity under pointer (for right-click) */
    function raycastFind(clientX: number, clientY: number): { eid: string | null; point: THREE.Vector3 | null } {
      const rect = el.getBoundingClientRect();
      const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const hits = raycaster.current.intersectObjects(scene.children, true);
      for (const hit of hits) {
        const eid = findEntityId(hit.object);
        if (eid) return { eid, point: hit.point };
      }
      return { eid: null, point: null };
    }

    const onPointerDown = (e: PointerEvent) => {
      // Left button (0): record click start, determine mode on move or up
      if (e.button === 0) {
        clickStart.current = { x: e.clientX, y: e.clientY, time: Date.now() };
        if (e.shiftKey || e.ctrlKey) {
          // Modifier held: immediately enter drag mode (no click detection)
          dragging.current = true;
          dragMode.current = e.shiftKey ? 'orbit' : 'zoom';
          lastPointer.current = { x: e.clientX, y: e.clientY };
          el.setPointerCapture(e.pointerId);
          clickStart.current = null; // cancel click detection
        }
        // Otherwise, wait for move/up to determine click vs drag
      }
      // Middle button (1): plain=pan, shift=orbit, ctrl+shift=orbit (free)
      if (e.button === 1) {
        e.preventDefault();
        dragging.current = true;
        dragMode.current = (e.shiftKey || e.ctrlKey) ? 'orbit' : 'pan';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
      // Right button (2) = orbit (drag) or context menu (click)
      if (e.button === 2) {
        clickStart.current = { x: e.clientX, y: e.clientY, time: Date.now() };
        dragging.current = true;
        dragMode.current = 'orbit';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }

      // Long-press detection (touch)
      if (e.pointerType === 'touch' && e.button === 0) {
        longPressStart = { x: e.clientX, y: e.clientY };
        longPressTimer = setTimeout(() => {
          if (!longPressStart) return;
          const { eid, point } = raycastFind(longPressStart.x, longPressStart.y);
          const hw = panelSize.current.w / 2;
          const hh = panelSize.current.h / 2;
          if (eid && eid !== 'panel-body') {
            // Long-press on entity could trigger edit too, but the plan says panel-body
          } else if (point) {
            onDblClickPanel(longPressStart.x, longPressStart.y, point.x + hw, point.y + hh);
          }
          cancelLongPress();
          // Cancel the ongoing click/drag
          clickStart.current = null;
          dragging.current = false;
          dragMode.current = 'none';
        }, 500);
      }
    };

    // Box select: project all entity meshes to screen and return IDs inside the rect
    function getEntitiesInBox(x1: number, y1: number, x2: number, y2: number): string[] {
      const rect = el.getBoundingClientRect();
      const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);
      const ids: string[] = [];
      const v = new THREE.Vector3();
      scene.traverse(obj => {
        const eid = obj.userData?.entityId as string | undefined;
        if (!eid || eid === 'panel-body') return;
        // Get world center of this mesh
        if ((obj as THREE.Mesh).geometry) {
          (obj as THREE.Mesh).geometry.computeBoundingBox();
          const bb = (obj as THREE.Mesh).geometry.boundingBox!;
          v.copy(bb.getCenter(new THREE.Vector3()));
          obj.localToWorld(v);
        } else {
          obj.getWorldPosition(v);
        }
        // Project to screen
        v.project(camera);
        const sx = (v.x * 0.5 + 0.5) * rect.width + rect.left;
        const sy = (-v.y * 0.5 + 0.5) * rect.height + rect.top;
        if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY) {
          ids.push(eid);
        }
      });
      // Deduplicate (parent + child may share entityId)
      return [...new Set(ids)];
    }

    const onPointerMove = (e: PointerEvent) => {
      // Cancel long-press if finger moved
      if (longPressStart) {
        const dx = Math.abs(e.clientX - longPressStart.x);
        const dy = Math.abs(e.clientY - longPressStart.y);
        if (dx > 10 || dy > 10) cancelLongPress();
      }

      // Check if left-button press just exceeded click threshold → start box select
      if (clickStart.current && !dragging.current) {
        const dx = Math.abs(e.clientX - clickStart.current.x);
        const dy = Math.abs(e.clientY - clickStart.current.y);
        if (dx >= CLICK_THRESHOLD || dy >= CLICK_THRESHOLD) {
          dragging.current = true;
          dragMode.current = 'boxSelect';
          boxStart.current = { x: clickStart.current.x, y: clickStart.current.y };
          el.setPointerCapture(e.pointerId);
          clickStart.current = null;
        }
        return;
      }

      // Update rubber-band box overlay
      if (dragMode.current === 'boxSelect' && boxStart.current) {
        const bx = boxStart.current.x, by = boxStart.current.y;
        onBoxSelectUpdate({
          x: Math.min(bx, e.clientX),
          y: Math.min(by, e.clientY),
          w: Math.abs(e.clientX - bx),
          h: Math.abs(e.clientY - by),
        });
        return;
      }

      if (!dragging.current) return;
      const mdx = e.clientX - lastPointer.current.x;
      const mdy = e.clientY - lastPointer.current.y;
      lastPointer.current = { x: e.clientX, y: e.clientY };

      const s = state.current;

      if (dragMode.current === 'orbit') {
        s.theta -= mdx * 0.005;
        s.phi = Math.max(0.01, Math.min(Math.PI - 0.01, s.phi + mdy * 0.005));
        onManualMove?.();
      } else if (dragMode.current === 'pan') {
        const panSpeed = s.dist * 0.002;
        const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
        const cp = Math.cos(s.phi), sp = Math.sin(s.phi);
        const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
        s.tx += (-mdx * rx + mdy * ux) * panSpeed;
        s.ty += mdy * uy * panSpeed;
        s.tz += (-mdx * rz + mdy * uz) * panSpeed;
        onManualMove?.();
      } else if (dragMode.current === 'zoom') {
        const factor = 1 + mdy * 0.005;
        s.dist = Math.max(1, Math.min(1_000_000, s.dist * factor));
        onManualMove?.();
      }
      inv();
    };

    const onPointerUp = (e: PointerEvent) => {
      cancelLongPress();

      // Was this a click (left button, < 4px movement)?
      if (clickStart.current && e.button === 0) {
        const dx = Math.abs(e.clientX - clickStart.current.x);
        const dy = Math.abs(e.clientY - clickStart.current.y);
        const dt = Date.now() - clickStart.current.time;
        if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD && dt < 500) {
          doRaycast(e.clientX, e.clientY, e.ctrlKey);
          inv(); // re-render to show highlight
        }
        clickStart.current = null;
      }

      // Right-click release without drag → context menu
      if (clickStart.current && e.button === 2) {
        const dx = Math.abs(e.clientX - clickStart.current.x);
        const dy = Math.abs(e.clientY - clickStart.current.y);
        if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) {
          const { eid, point } = raycastFind(e.clientX, e.clientY);
          const hw = panelSize.current.w / 2;
          const hh = panelSize.current.h / 2;
          if (eid && eid !== 'panel-body') {
            const px = point ? point.x + hw : 0;
            const py = point ? point.y + hh : 0;
            onRightClickEntity(eid, e.clientX, e.clientY, px, py);
          } else if (point) {
            onRightClickPanel(e.clientX, e.clientY, point.x + hw, point.y + hh);
          }
        }
        clickStart.current = null;
      }

      // Finish box select
      if (dragMode.current === 'boxSelect' && boxStart.current && e.button === 0) {
        const ids = getEntitiesInBox(boxStart.current.x, boxStart.current.y, e.clientX, e.clientY);
        onBoxSelectUpdate(null); // hide overlay
        if (ids.length > 0) {
          onBoxSelectEntities(ids, e.ctrlKey);
        } else {
          onClickEmpty();
        }
        boxStart.current = null;
        inv();
      }
      dragging.current = false;
      dragMode.current = 'none';
      try { el.releasePointerCapture(e.pointerId); } catch {/* ignore */}
    };

    const onWheel = (e: WheelEvent) => {
      const s = state.current;
      const factor = e.deltaY > 0 ? 1.1 : 0.9;
      s.dist = Math.max(1, Math.min(1_000_000, s.dist * factor));
      onManualMove?.();
      inv();
    };

    const onContextMenu = (e: Event) => e.preventDefault();
    const onAuxClick = (e: MouseEvent) => { if (e.button === 1) e.preventDefault(); };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('auxclick', onAuxClick);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('auxclick', onAuxClick);
    };
  }, [gl, camera, scene, invalidate, onSelect, onClickEmpty, onBoxSelectUpdate, onBoxSelectEntities, onDblClickEntity, onDblClickPanel, onRightClickEntity, onRightClickPanel, panelSize]);

  return null;
}

// ── Viewport toolbar (view presets + shading + delete) ───────────────────────
function ViewportToolbar({ onCenterView, onZoomIn, onZoomOut, onPanLeft, onPanRight, onPanUp, onPanDown, onDownloadDebug, selectionCount, onDeleteSelection }: {
  onCenterView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPanLeft: () => void;
  onPanRight: () => void;
  onPanUp: () => void;
  onPanDown: () => void;
  onDownloadDebug: () => void;
  selectionCount: number;
  onDeleteSelection: () => void;
}) {
  const { ui, setViewPreset, setShadingMode, toggleGrid, toggleGround, togglePanelMarks } = useShallowStore(s => ({
    ui:            s.ui,
    setViewPreset: s.setViewPreset,
    setShadingMode:s.setShadingMode,
    toggleGrid:    s.toggleGrid,
    toggleGround:  s.toggleGround,
    togglePanelMarks: s.togglePanelMarks,
  }));

  const pb = (id: ViewPreset, label: string, title: string) => (
    <button key={id} className={`vt-btn${ui.viewPreset === id ? ' active' : ''}`} onClick={() => setViewPreset(id)} title={title}>{label}</button>
  );

  return (
    <div className="viewport-toolbar">
      <div className="vt-grid-3x3">
        {pb('bl-iso', '\u25CF', 'Back-Left Iso')}{pb('back', 'B', 'Back')}{pb('br-iso', '\u25CF', 'Back-Right Iso')}
        {pb('left', 'L', 'Left')}<button className={`vt-btn${ui.viewPreset === null ? ' active' : ''}`} onClick={onCenterView} title="Zoom Extents">{'\u25A0'}</button>{pb('right', 'R', 'Right')}
        {pb('fl-iso', '\u25CF', 'Front-Left Iso')}{pb('front', 'F', 'Front')}{pb('fr-iso', '\u25CF', 'Front-Right Iso')}
      </div>
      <div className="vt-group">
        {pb('top', 'TP', 'Top')}{pb('bottom', 'BT', 'Bottom')}
      </div>
      <div className="vt-group">
        <button className="vt-btn" onClick={onZoomIn} title="Zoom In">+</button>
        <button className="vt-btn" onClick={onZoomOut} title="Zoom Out">−</button>
        <span className="vt-sep">│</span>
        <button className="vt-btn" onClick={onPanLeft} title="Pan Left">◀</button>
        <button className="vt-btn" onClick={onPanRight} title="Pan Right">▶</button>
        <button className="vt-btn" onClick={onPanUp} title="Pan Up">▲</button>
        <button className="vt-btn" onClick={onPanDown} title="Pan Down">▼</button>
      </div>
      {selectionCount > 0 && (
        <div className="vt-group">
          <button
            className="vt-btn vt-btn-danger"
            onClick={onDeleteSelection}
            title={`Delete ${selectionCount} selected entit${selectionCount > 1 ? 'ies' : 'y'}`}
          >
            🗑 {selectionCount}
          </button>
        </div>
      )}
      <div className="vt-group">
        <button className="vt-btn" onClick={onDownloadDebug} title="Download camera debug log" style={{ fontSize: 10, color: '#999' }}>⬇ Log</button>
      </div>
      <div className="vt-group vt-shading">
        {(['wireframe', 'hidden', 'shaded'] as ShadingMode[]).map(m => (
          <button key={m} className={`vt-btn${ui.shadingMode === m ? ' active' : ''}`} onClick={() => setShadingMode(m)}>
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>
      <div className="vt-group">
        <button className={`vt-btn${ui.showGrid ? ' active' : ''}`} onClick={toggleGrid} title="Toggle grid">Grid</button>
        <button className={`vt-btn${ui.showGround ? ' active' : ''}`} onClick={toggleGround} title="Toggle ground plane">Ground</button>
        <button className={`vt-btn${ui.showPanelMarks ? ' active' : ''}`} onClick={togglePanelMarks} title="Toggle panel marks">Marks</button>
      </div>
    </div>
  );
}

// ── Main exported component ───────────────────────────────────────────────────
export default function PanelViewer3D() {
  const { ui, project, selectEntity, toggleSelectEntity, setSelection, addToSelection, clearSelection, deleteSelectedEntities, setViewPreset, clearViewPreset, startPlacement, showContextMenu, editEntity: storeEditEntity } = useShallowStore(s => ({
    ui: s.ui, project: s.project,
    selectEntity: s.selectEntity, toggleSelectEntity: s.toggleSelectEntity,
    setSelection: s.setSelection, addToSelection: s.addToSelection,
    clearSelection: s.clearSelection, deleteSelectedEntities: s.deleteSelectedEntities,
    setViewPreset: s.setViewPreset, clearViewPreset: s.clearViewPreset,
    startPlacement: s.startPlacement, showContextMenu: s.showContextMenu,
    editEntity: s.editEntity,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);

  // Find panel's elevation and compute joint trims
  const panelTrims = useMemo(() => {
    if (!panel || !project) return { leftTrim: 0, rightTrim: 0 };
    const elev = project.elevations.find(e => e.panelIds.includes(panel.id));
    if (!elev) return { leftTrim: 0, rightTrim: 0 };
    return computeEdgeTrims(panel.id, elev, project.settings.jointWidth);
  }, [panel, project]);

  const [resetKey, setResetKey] = useState(0);
  // Track last non-null preset so center-view (⊙) can re-apply it
  const lastPresetRef = useRef<ViewPreset>('fl-iso');
  if (ui.viewPreset) lastPresetRef.current = ui.viewPreset;
  const debugLog = useRef<string[]>([]);
  const zoomRef = useRef<((factor: number) => void) | null>(null);
  const panRef = useRef<((dx: number, dy: number) => void) | null>(null);
  const viewerRef = useRef<HTMLDivElement>(null);

  // Stable ref for panel size — no re-render on change
  const panelSize = useRef({ w: 120, h: 168 });
  if (panel) panelSize.current = { w: panel.mpvar.width || 120, h: panel.mpvar.height || 168 };
  const gridExtent = useMemo(() => snapGridExtent(0, panel?.mpvar.width || 120, 0, 0), [panel?.mpvar.width]);

  // Selection callbacks (stable refs to avoid re-registering pointer events)
  const onSelect = useCallback((entityId: string, ctrlKey: boolean) => {
    if (ctrlKey) toggleSelectEntity(entityId);
    else selectEntity(entityId);
  }, [selectEntity, toggleSelectEntity]);

  const onClickEmpty = useCallback(() => { clearSelection(); }, [clearSelection]);

  // Click-to-place callbacks (stable refs)
  const onDblClickEntity = useCallback((entityId: string) => { storeEditEntity(entityId); }, [storeEditEntity]);
  const onDblClickPanel = useCallback((screenX: number, screenY: number, panelX: number, panelY: number) => {
    startPlacement(panelX, panelY, screenX, screenY);
  }, [startPlacement]);
  const onRightClickEntity = useCallback((entityId: string, screenX: number, screenY: number, panelX: number, panelY: number) => {
    showContextMenu(entityId, screenX, screenY, panelX, panelY);
  }, [showContextMenu]);
  const onRightClickPanel = useCallback((screenX: number, screenY: number, panelX: number, panelY: number) => {
    showContextMenu(null, screenX, screenY, panelX, panelY);
  }, [showContextMenu]);

  // Box select state (rubber-band overlay)
  const [boxRect, setBoxRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const onBoxSelectUpdate = useCallback((box: { x: number; y: number; w: number; h: number } | null) => {
    setBoxRect(box);
  }, []);

  const onBoxSelectEntities = useCallback((entityIds: string[], ctrlKey: boolean) => {
    if (ctrlKey) addToSelection(entityIds);
    else setSelection(entityIds);
  }, [setSelection, addToSelection]);

  // Delete handler with confirm when > 1
  const handleDeleteSelection = useCallback(() => {
    const n = ui.selectedEntities.length;
    if (n === 0) return;
    if (n > 1 && !window.confirm(`Delete ${n} selected entities?`)) return;
    deleteSelectedEntities();
  }, [ui.selectedEntities.length, deleteSelectedEntities]);

  // Keyboard: Delete = delete selection, Escape = clear selection
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (ui.selectedEntities.length > 0) {
          e.preventDefault();
          handleDeleteSelection();
        }
      }
      if (e.key === 'Escape') {
        clearSelection();
      }
    };
    const el = viewerRef.current;
    if (el) el.addEventListener('keydown', onKeyDown);
    return () => { if (el) el.removeEventListener('keydown', onKeyDown); };
  }, [ui.selectedEntities.length, handleDeleteSelection, clearSelection]);

  const downloadDebugLog = useCallback(() => {
    const blob = new Blob([debugLog.current.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cv-camera-debug-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="viewer-root" ref={viewerRef} tabIndex={-1}>
      <ViewportToolbar
        onCenterView={() => { setViewPreset(lastPresetRef.current); setResetKey(k => k + 1); }}
        onZoomIn={() => { zoomRef.current?.(0.8); clearViewPreset(); }}
        onZoomOut={() => { zoomRef.current?.(1.25); clearViewPreset(); }}
        onPanLeft={() => { panRef.current?.(30, 0); clearViewPreset(); }}
        onPanRight={() => { panRef.current?.(-30, 0); clearViewPreset(); }}
        onPanUp={() => { panRef.current?.(0, -30); clearViewPreset(); }}
        onPanDown={() => { panRef.current?.(0, 30); clearViewPreset(); }}
        onDownloadDebug={downloadDebugLog}
        selectionCount={ui.selectedEntities.length}
        onDeleteSelection={handleDeleteSelection}
      />
      <Canvas
        frameloop="demand"
        camera={{ fov: 45, near: 0.1, far: 1_000_000, position: [0, 0, 500] }}
        gl={{ antialias: true }}
        shadows
        style={{ background: '#0d1117' }}
      >
        <CameraLock
          preset={ui.viewPreset}
          panelId={panel?.id}
          panelSize={panelSize}
          resetKey={resetKey}
          debugLog={debugLog}
          zoomRef={zoomRef}
          panRef={panRef}
          onSelect={onSelect}
          onClickEmpty={onClickEmpty}
          onBoxSelectUpdate={onBoxSelectUpdate}
          onBoxSelectEntities={onBoxSelectEntities}
          onManualMove={clearViewPreset}
          onDblClickEntity={onDblClickEntity}
          onDblClickPanel={onDblClickPanel}
          onRightClickEntity={onRightClickEntity}
          onRightClickPanel={onRightClickPanel}
        />

        <ambientLight intensity={0.6} />
        <directionalLight position={[3000, 5000, 2000]} intensity={1.2} castShadow />
        <directionalLight position={[-2000, 1000, -1000]} intensity={0.4} />

        {ui.shadingMode === 'shaded' && (
          <Environment preset="warehouse" background={false} />
        )}

        {panel ? (
          <PanelMesh3D
            panel={panel}
            shading={ui.shadingMode}
            showPanelMarks={ui.showPanelMarks}
            selectedEntities={new Set(ui.selectedEntities)}
            layerVisibility={ui.layerVisibility}
            leftTrim={panelTrims.leftTrim}
            rightTrim={panelTrims.rightTrim}
          />
        ) : (
          <mesh>
            <boxGeometry args={[240, 360, 8]} />
            <meshStandardMaterial color={0x334455} wireframe />
          </mesh>
        )}

        <Grid
          args={[gridExtent.width, gridExtent.depth]}
          cellSize={120}
          sectionSize={600}
          cellColor="#3a5a80"
          sectionColor="#5080b0"
          cellThickness={1.5}
          sectionThickness={2.5}
          fadeDistance={8000}
          visible={ui.showGrid}
          position={[gridExtent.cx, 0, gridExtent.cz]}
        />

        {ui.showGrid && <GridLabels extent={gridExtent} />}

        {/* Ground plane */}
        {ui.showGround && (
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[gridExtent.cx, -0.1, gridExtent.cz]}
            receiveShadow
          >
            <planeGeometry args={[gridExtent.width, gridExtent.depth]} />
            <meshStandardMaterial
              color="#8b7355"
              roughness={0.95}
              metalness={0.0}
            />
          </mesh>
        )}

        <NorthArrow />
      </Canvas>

      {!panel && (
        <div className="viewer-no-panel">
          No panel selected — create or select a panel.
        </div>
      )}

      {boxRect && (
        <div className="box-select-overlay" style={{
          left: boxRect.x, top: boxRect.y,
          width: boxRect.w, height: boxRect.h,
        }} />
      )}
    </div>
  );
}
